import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const { LuShare2, LuPlus, LuTrash2, LuEdit, LuX, LuSave, LuImage, LuUpload } = LuIcons;

const AdminSocialLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({ platform: '', url: '', icon: 'LuFacebook', custom_image: '', sort_order: 0, is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    const { data } = await supabase.from('social_links').select('*').order('sort_order', { ascending: true });
    if (data) setLinks(data);
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `social_${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('images').upload(fileName, file);
      if (error) throw error;
      const url = supabase.storage.from('images').getPublicUrl(fileName).data.publicUrl;
      setFormData(prev => ({ ...prev, custom_image: url }));
    } catch (err) {
      alert('خطأ في رفع الصورة: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      await supabase.from('social_links').update(formData).eq('id', editingId);
    } else {
      await supabase.from('social_links').insert([formData]);
    }
    setIsAdding(false);
    setEditingId(null);
    setFormData({ platform: '', url: '', icon: 'LuFacebook', custom_image: '', sort_order: 0, is_active: true });
    fetchLinks();
  };

  const handleDelete = async (id) => {
    if (window.confirm('حذف هذا الرابط؟')) {
      await supabase.from('social_links').delete().eq('id', id);
      fetchLinks();
    }
  };

  const toggleStatus = async (link) => {
    await supabase.from('social_links').update({ is_active: !link.is_active }).eq('id', link.id);
    fetchLinks();
  };

  const ICONS = ['LuFacebook', 'LuInstagram', 'LuTwitter', 'LuYoutube', 'LuSend', 'LuMusic', 'LuLinkedin', 'LuGlobe'];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800 flex items-center">
          <SafeIcon icon={LuShare2} className="w-6 h-6 me-2 text-emerald-600" /> روابط التواصل الاجتماعي
        </h2>
        {!isAdding && !editingId && (
          <button onClick={() => setIsAdding(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl font-medium transition-colors flex items-center gap-2">
            <SafeIcon icon={LuPlus} className="w-5 h-5" /> إضافة رابط
          </button>
        )}
      </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="mb-8 bg-slate-50 p-5 rounded-2xl border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-800">{editingId ? 'تعديل الرابط' : 'إضافة رابط جديد'}</h3>
            <button type="button" onClick={() => { setIsAdding(false); setEditingId(null); }} className="p-1.5 text-slate-400 hover:text-slate-600 bg-slate-200 rounded-lg">
              <SafeIcon icon={LuX} className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">المنصة (مثال: تيك توك)</label>
              <input required type="text" value={formData.platform} onChange={e => setFormData({...formData, platform: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">الرابط (URL)</label>
              <input required type="url" value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} dir="ltr" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-left" placeholder="https://..." />
            </div>
            
            {/* Custom Image Section */}
            <div className="md:col-span-2 bg-white p-4 rounded-xl border border-slate-200">
              <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <SafeIcon icon={LuImage} className="w-4 h-4 text-emerald-500" /> لوجو مخصص (اختياري)
              </label>
              <p className="text-xs text-slate-500 mb-3">إذا لم تجد الأيقونة المناسبة في القائمة، يمكنك رفع صورة/لوجو الخاص بالمنصة (يفضل بصيغة PNG شفافة).</p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {formData.custom_image ? (
                    <img src={formData.custom_image} alt="logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <SafeIcon icon={LuImage} className="w-6 h-6 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input type="file" accept="image/*" ref={imageRef} onChange={handleUpload} className="hidden" />
                  <button type="button" onClick={() => imageRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg text-sm font-bold transition-colors">
                    <SafeIcon icon={uploading ? LuX : LuUpload} className="w-4 h-4" /> {uploading ? 'جاري الرفع...' : 'رفع صورة'}
                  </button>
                  {formData.custom_image && (
                    <button type="button" onClick={() => setFormData(p => ({ ...p, custom_image: '' }))} className="text-xs text-red-500 hover:text-red-700">إزالة الصورة المخصصة</button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">أو اختر أيقونة جاهزة</label>
              <div className="flex flex-wrap gap-2">
                {ICONS.map(iconName => (
                  <button key={iconName} type="button" onClick={() => setFormData({...formData, icon: iconName, custom_image: ''})} className={`p-2 rounded-lg border-2 transition-colors ${formData.icon === iconName && !formData.custom_image ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
                    <SafeIcon icon={LuIcons[iconName]} className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">الترتيب</label>
              <input type="number" value={formData.sort_order} onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value)})} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
            </div>
          </div>
          <button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2">
            <SafeIcon icon={LuSave} className="w-4 h-4" /> حفظ
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : (
        <div className="space-y-3">
          {links.map(link => (
            <div key={link.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex gap-4 items-center flex-1">
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center rounded-xl flex-shrink-0">
                  {link.custom_image ? (
                    <img src={link.custom_image} alt={link.platform} className="w-8 h-8 object-contain" />
                  ) : (
                    <SafeIcon icon={LuIcons[link.icon]} className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">{link.platform}</h3>
                  <a href={link.url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mt-0.5 block" dir="ltr">{link.url}</a>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleStatus(link)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${link.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                  {link.is_active ? 'نشط' : 'مخفي'}
                </button>
                <button onClick={() => { setFormData(link); setEditingId(link.id); setIsAdding(false); }} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
                  <SafeIcon icon={LuEdit} className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(link.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                  <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {links.length === 0 && !isAdding && <p className="text-center text-slate-500 py-6">لا توجد روابط.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminSocialLinks;